// Command validation & application — docs/ARCHITECTURE_M0.md §4/§5.
// Invalid commands never throw mid-tick; they are recorded in report.rejectedCommands.

import type { ChipProject, Command, Discipline, ResearchGrade, TechNodeId } from "./types.js";
import { DISCIPLINES, GARAGE_ACTIVITIES, LIVE_PHASES } from "./types.js";
import type { TickContext } from "./ctx.js";
import { charge, emit } from "./ctx.js";
import { getMarket, getNode } from "./content.js";
import { interpolate } from "./calendar.js";
import { coverageOf, planProjectEffort } from "./formulas/effort.js";
import { closeRound } from "./systems/finance.js";
import { activeTree, getTechNode, gradeMult, isResearched, prereqsMet } from "./systems/tree.js";
import { incorporate, runPitch } from "./systems/garage.js";
import { fileS1, priceIpo } from "./systems/ipo.js";
import { buyback, secondaryOffering } from "./systems/street.js";
import { answerAsk, customerDelta, fabCycleDelta, foundryDelta } from "./systems/stakeholders.js";
import { hireFillRate, laborHeat, moraleHit } from "./systems/talent.js";
import { edaMethodMult } from "./systems/tools.js";
import { acquireRival } from "./systems/ma.js";

const MAX_HIRE_PER_COMMAND = 50;
export const MAX_STAFF_PER_PROJECT = 500;
export const MIN_RAISE = 100_000;
export const MAX_RAISE = 500_000_000; // private checkbooks have a ceiling; secondaries scale with market cap instead
const MAX_DIE_AREA_MM2 = 400;

export function applyCommands(ctx: TickContext, commands: Command[]): void {
  for (const command of commands) {
    const reason = applyOne(ctx, command);
    if (reason !== null) ctx.rejected.push({ command, reason });
  }
}

function isPosInt(x: unknown): x is number {
  return typeof x === "number" && Number.isInteger(x) && x > 0;
}

function findProject(ctx: TickContext, id: string): ChipProject | undefined {
  return ctx.s.projects.find((p) => p.id === id);
}

function poolSize(ctx: TickContext, d: Discipline): { productive: number; ramping: number } {
  const pool = ctx.s.people.pools[d];
  return { productive: pool.productive, ramping: pool.ramping.reduce((a, r) => a + r.count, 0) };
}

/** Commands that exist before the company does (P0, pivot spec §3). */
const GARAGE_COMMANDS: ReadonlySet<Command["type"]> = new Set([
  "setGarageAlloc",
  "schedulePitch",
  "foldCompany",
  "acceptTermSheet",
  "rejectTermSheet",
]);

/** Returns null on success, otherwise a rejection reason. */
function applyOne(ctx: TickContext, command: Command): string | null {
  const s = ctx.s;
  const eco = ctx.content.economy;
  if (s.stage === "garage" && !GARAGE_COMMANDS.has(command.type)) {
    return "the company doesn't exist yet — you're three people in a booth at Denny's";
  }
  switch (command.type) {
    case "hire": {
      if (!DISCIPLINES.includes(command.discipline)) return "unknown discipline";
      if (!isPosInt(command.count) || command.count > MAX_HIRE_PER_COMMAND) {
        return `count must be integer 1..${MAX_HIRE_PER_COMMAND}`;
      }
      if (s.board.hiringFreeze) return "the board has frozen hiring until confidence recovers";
      const annual = salaryOf(ctx, command.discipline);
      // recruiting in a hot labor market costs a knife-fight premium — and
      // below-market offers simply don't fill (M3, design §9)
      const heatMult = 1 + eco.talent.heatRecruitCoef * laborHeat(ctx);
      const filled = Math.max(1, Math.round(command.count * hireFillRate(ctx)));
      charge(
        ctx,
        "recruiting",
        -filled * annual * eco.recruitingFeePctAnnual * heatMult,
        `recruiting ${filled} ${command.discipline}${filled < command.count ? ` (${command.count - filled} offers declined)` : ""}`,
      );
      s.people.pendingHires.push({ discipline: command.discipline, count: filled });
      return null;
    }
    case "fire": {
      if (!DISCIPLINES.includes(command.discipline)) return "unknown discipline";
      if (!isPosInt(command.count)) return "count must be a positive integer";
      const { productive, ramping } = poolSize(ctx, command.discipline);
      if (command.count > productive + ramping) return "cannot fire more than current pool";
      const pool = s.people.pools[command.discipline];
      let left = command.count;
      // ramping first, then productive
      for (const r of pool.ramping) {
        const take = Math.min(r.count, left);
        r.count -= take;
        left -= take;
      }
      pool.ramping = pool.ramping.filter((r) => r.count > 0);
      pool.productive -= left;
      const monthly = (salaryOf(ctx, command.discipline) / 12) * eco.overheadBurden;
      charge(ctx, "severance", -command.count * monthly * eco.severanceMonths, `severance ${command.count} ${command.discipline}`);
      // layoffs land on everyone who stayed, scale-aware (M3, design §9)
      const heads = DISCIPLINES.reduce((a, d) => {
        const p = s.people.pools[d];
        return a + p.productive + p.ramping.reduce((x, r) => x + r.count, 0);
      }, 0);
      const td = eco.talent;
      moraleHit(ctx, -Math.min(td.layoffMoraleHitMax, (td.layoffMoraleCoef * command.count) / Math.max(1, heads + command.count)));
      return null;
    }
    case "setNre": {
      const cap = eco.nre.maxEngineers;
      if (!Number.isInteger(command.engineers) || command.engineers < 0 || command.engineers > cap) {
        return `engineers must be integer 0..${cap} (customer demand is finite)`;
      }
      s.nreEngineers = command.engineers;
      return null;
    }
    case "startNodeQual": {
      let node;
      try {
        node = getNode(ctx.content, command.nodeId);
      } catch {
        return "unknown node";
      }
      const access = s.nodes[command.nodeId]!;
      if (access.status !== "locked") return `node is ${access.status}`;
      if (node.availableMonth > s.monthIndex) return "node not yet available from foundry";
      charge(ctx, "nodeQualFee", -node.qual.fee, `qualification engagement ${node.displayName}`);
      access.status = "qualifying";
      access.qualEmRemaining = node.qual.engMonths;
      access.qualMonthsRemaining = node.qual.durationMonths;
      return null;
    }
    case "setYieldResearch": {
      if (!(command.nodeId in s.nodes)) return "unknown node";
      const access = s.nodes[command.nodeId]!;
      if (access.status !== "qualified") return "yield research requires a qualified node";
      if (!Number.isInteger(command.engineers) || command.engineers < 0 || command.engineers > MAX_STAFF_PER_PROJECT) {
        return "engineers must be a non-negative integer";
      }
      access.researchStaff = command.engineers;
      return null;
    }
    case "setReliability": {
      if (!(command.nodeId in s.nodes)) return "unknown node";
      const access = s.nodes[command.nodeId]!;
      if (access.status !== "qualified") return "reliability engineering requires a qualified node";
      if (!Number.isInteger(command.engineers) || command.engineers < 0 || command.engineers > MAX_STAFF_PER_PROJECT) {
        return "engineers must be a non-negative integer";
      }
      access.reliabilityStaff = command.engineers;
      return null;
    }
    case "startProject": {
      let market;
      try {
        market = getMarket(ctx.content, command.marketId);
      } catch {
        return "unknown market";
      }
      if (!(command.nodeId in s.nodes)) return "unknown node";
      const access = s.nodes[command.nodeId]!;
      if (access.status === "locked") return "node must be qualifying or qualified";
      if (!isPosInt(command.kGates)) return "kGates must be a positive integer";
      const models = eco.models;
      const node = getNode(ctx.content, command.nodeId);
      const area = ((command.kGates * 1000) / node.gateDensity) * (1 + models.dieOverhead);
      if (area > MAX_DIE_AREA_MM2) {
        return `die would be ${area.toFixed(0)}mm² on ${node.displayName}; max manufacturable is ${MAX_DIE_AREA_MM2}mm²`;
      }
      // the analog/digital fork (design §5): the first chip in a path-affine market
      // locks the company's path; later off-path work is allowed but taxed
      if (market.pathAffinity !== "neutral" && s.path === null) {
        s.path = market.pathAffinity;
        emit(ctx, "pathLocked", `First ${market.displayName} chip locks the company onto the ${s.path} path`, {
          path: s.path,
          marketId: market.id,
        });
      }
      const offPath = market.pathAffinity !== "neutral" && s.path !== null && market.pathAffinity !== s.path;
      // licensed IP (M3, design §6.4): you integrate instead of designing —
      // effort and bug exposure shrink; complexity credit and die stay full-size
      const ipIds = command.ipIds ?? [];
      if (ipIds.length > 2) return "at most 2 IP licenses per chip";
      if (new Set(ipIds).size !== ipIds.length) return "duplicate IP license";
      let ipFrac = 0;
      let royaltyPct = 0;
      let lambdaMult = 1;
      let ipUpfrontTotal = 0;
      for (const ipId of ipIds) {
        const ip = ctx.content.ip.find((x) => x.id === ipId);
        if (!ip) return `unknown IP ${ipId}`;
        if (ip.availableFrom > s.monthIndex) return `${ip.displayName} is not on the market yet`;
        ipFrac += ip.kGatesFrac;
        royaltyPct += ip.royaltyPct;
        lambdaMult *= ip.lambdaMult;
        ipUpfrontTotal += interpolate(ip.upfront, s.monthIndex);
      }
      ipFrac = Math.min(0.5, ipFrac);
      const kGatesEffective = Math.max(1, Math.round(command.kGates * (1 - ipFrac)));
      // architecture (P0, pivot spec §7): assembled from RESEARCHED tree nodes,
      // snapshotted with their grades — mid-project research doesn't retrofit
      const archIds = command.archNodeIds ?? [];
      const tree = activeTree(ctx);
      if (archIds.length > 0) {
        if (!tree) return "this scenario has no architecture tree";
        if (new Set(archIds).size !== archIds.length) return "duplicate architecture node";
        for (const id of archIds) {
          try {
            getTechNode(tree, id);
          } catch {
            return `unknown architecture node ${id}`;
          }
          if (!isResearched(ctx, id)) return `${id} is not researched yet`;
        }
      }
      if (market.techGatesAnyOf && market.techGatesAnyOf.length > 0) {
        if (!market.techGatesAnyOf.some((id) => isResearched(ctx, id))) {
          return `${market.displayName} requires one of: ${market.techGatesAnyOf.join(", ")}`;
        }
      }
      const archNodes: { id: TechNodeId; grade: ResearchGrade }[] = archIds.map((id) => ({
        id,
        grade: s.tree.researched[id]!.grade,
      }));
      const plan = planProjectEffort(kGatesEffective, s.monthIndex, models, offPath, edaMethodMult(ctx));
      if (ipUpfrontTotal > 0) charge(ctx, "ipUpfront", -ipUpfrontTotal, `IP license fees for prj-${s.counters.nextProject}`);
      const id = `prj-${s.counters.nextProject}`;
      s.counters.nextProject += 1;
      // hidden entropy: the true bug rate is never exactly the public formula
      // (mean-preserving lognormal; the coverage gauge stays honest-but-uncertain)
      const sigma = models.hiddenLambdaSigma;
      const lambdaNoise = ctx.rng.logNormal(-(sigma * sigma) / 2, sigma) * lambdaMult;
      s.projects.push({
        id,
        marketId: command.marketId,
        nodeId: command.nodeId,
        kGates: command.kGates,
        ipIds,
        kGatesEffective,
        royaltyPct,
        blocks: plan.blocks,
        startedMonth: s.monthIndex,
        offPath,
        phase: "feasibility",
        staffing: { design: 0, verification: 0, implementation: 0 },
        feasEmDone: 0,
        feasMonthsLeft: models.feasMonths,
        designEmDone: 0,
        designEmTotal: plan.designEmTotal,
        implEmDone: 0,
        implEmTotal: plan.implEmTotal,
        verifEmDone: 0,
        verifScale: plan.verifScale,
        covAtTapein: null,
        rtlReworks: 0,
        requirementsFit: null,
        implQualityRoll: null,
        dieAreaMm2: null,
        fabMonthsLeft: 0,
        validationMonthsLeft: 0,
        respinFixEmRemaining: 0,
        respinFixMonthsLeft: 0,
        prodQualMonthsLeft: 0,
        respins: 0,
        errataCount: 0,
        prodQualStalled: false,
        respinFixStalled: false,
        archNodes,
        hidden: {
          critLambda: plan.critLambda * lambdaNoise,
          minorLambda: plan.minorLambda * lambdaNoise,
          escapeMult: 1,
        },
      });
      emit(ctx, "projectStarted", `${id} started: ${command.kGates} kG in ${market.displayName} on ${node.displayName}`, {
        projectId: id,
        marketId: command.marketId,
        nodeId: command.nodeId,
        kGates: command.kGates,
        blocks: plan.blocks,
        offPath,
        startedMonth: s.monthIndex,
      });
      return null;
    }
    case "setStaffing": {
      const project = findProject(ctx, command.projectId);
      if (!project) return "unknown project";
      if (!LIVE_PHASES.includes(project.phase)) return `project is ${project.phase}`;
      for (const d of DISCIPLINES) {
        const v = command.staffing[d];
        if (typeof v !== "number" || !Number.isInteger(v) || v < 0 || v > MAX_STAFF_PER_PROJECT) {
          return `staffing.${d} must be integer 0..${MAX_STAFF_PER_PROJECT}`;
        }
      }
      // rebuild from validated keys only — never spread unvalidated command data into state
      project.staffing = {
        design: command.staffing.design!,
        verification: command.staffing.verification!,
        implementation: command.staffing.implementation!,
      };
      return null;
    }
    case "tapeIn": {
      const project = findProject(ctx, command.projectId);
      if (!project) return "unknown project";
      if (project.phase !== "waitingTapein") return `project is ${project.phase}, not waiting for tape-in`;
      const models = eco.models;
      const implNeeded = models.tapeinMinImplFrac * project.implEmTotal;
      if (project.implEmDone < implNeeded) {
        return `physical design not ready: needs ${implNeeded.toFixed(1)} implementation EM (floorplan/constraints), has ${project.implEmDone.toFixed(1)}`;
      }
      // netlist freeze: coverage is snapshotted — everything still uncovered is
      // the hazard that layout-time verification kicks the chip back to design
      project.covAtTapein = coverageOf(project.verifEmDone, project.verifScale);
      project.phase = "layout";
      emit(ctx, "tapein", `${project.id} taped in — netlist frozen, layout begins`, {
        projectId: project.id,
        coverage: project.covAtTapein,
        rtlReworks: project.rtlReworks,
      });
      return null;
    }
    case "tapeOut": {
      const project = findProject(ctx, command.projectId);
      if (!project) return "unknown project";
      if (project.phase !== "waitingTapeout") return `project is ${project.phase}, not waiting for tape-out`;
      const access = s.nodes[project.nodeId]!;
      if (access.status !== "qualified") return "node not qualified yet";
      const node = getNode(ctx.content, project.nodeId);
      charge(ctx, "maskNre", -node.maskSetCost, `mask set ${project.id} on ${node.displayName}`);
      // the emulation gambit (P0, graphics_history.md §5.7): a completed
      // hardware-emulation capability runs the whole design against real
      // software pre-tapeout — a coverage-space boost, grade-scaled, applied
      // at the moment you commit masks. This is how first silicon ships.
      const emuTree = activeTree(ctx);
      if (emuTree) {
        for (const tn of emuTree.nodes) {
          if (tn.tapeoutCoverageBoost && isResearched(ctx, tn.id)) {
            const grade = s.tree.researched[tn.id]!.grade;
            const boost = Math.min(0.85, tn.tapeoutCoverageBoost * gradeMult(ctx, grade));
            project.verifEmDone += -Math.log(1 - boost) * project.verifScale;
          }
        }
      }
      // fab cycle rides the foundry relationship: hot lots for good accounts (M3 §11)
      project.fabMonthsLeft = Math.max(1, node.fabCycleMonths + fabCycleDelta(ctx));
      foundryDelta(ctx, "tapeout");
      project.phase = "fab";
      // masks out → the team disbands: nothing to staff while wafers are in fab,
      // and the validation/respin/prodQual crews get assigned deliberately later
      project.staffing = { design: 0, verification: 0, implementation: 0 };
      emit(ctx, "tapeout", `${project.id} taped out on ${node.displayName}`, {
        projectId: project.id,
        maskCost: node.maskSetCost,
        respin: false,
      });
      return null;
    }
    case "respin": {
      const project = findProject(ctx, command.projectId);
      if (!project) return "unknown project";
      if (project.phase !== "respinDecision") return "project has no pending respin decision";
      const models = eco.models;
      project.phase = "respinFix";
      project.respins += 1;
      project.respinFixEmRemaining = models.respinFixEm;
      project.respinFixMonthsLeft = models.respinFixMinMonths;
      project.hidden.escapeMult *= models.respinEscapeMult;
      // silicon debug reveals bugs: coverage jumps toward truth by respinCoverageBoost
      // (in coverage space: 1-cov' = (1-cov)(1-boost), i.e. verifEm += -ln(1-boost)*scale)
      project.verifEmDone += -Math.log(1 - models.respinCoverageBoost) * project.verifScale;
      emit(ctx, "respinStarted", `${project.id} respin #${project.respins}`, {
        projectId: project.id,
        respins: project.respins,
      });
      return null;
    }
    case "cancelProject": {
      const project = findProject(ctx, command.projectId);
      if (!project) return "unknown project";
      if (!LIVE_PHASES.includes(project.phase)) return `project is ${project.phase}`;
      project.phase = "cancelled";
      emit(ctx, "projectCancelled", `${project.id} cancelled`, { projectId: project.id });
      return null;
    }
    case "endOfLifeProduct": {
      const product = s.products.find((p) => p.id === command.productId);
      if (!product) return "unknown product";
      if (!product.active) return "product already end-of-lifed";
      product.active = false;
      product.share = 0;
      // distributor return rights (design §10): stock stranded in the channel
      // comes back for credit — sell-in you booked is revenue you now refund.
      // This is what stops "stuff, then EOL" from being free money.
      let returned = 0;
      if (product.channelUnits > 0 && product.lastAsp !== null) {
        returned = Math.round(product.channelUnits * product.lastAsp * eco.channel.eolReturnRate);
        if (returned > 0) {
          charge(ctx, "channelReturns", -returned, `${product.id} EOL channel returns`);
          ctx.cogs += returned; // margin-honest, like price protection
        }
      }
      product.channelUnits = 0;
      // killing a part customers still design around costs the relationship (M3 §11)
      const marketDef = getMarket(ctx.content, product.marketId);
      const tamNow = interpolate(marketDef.tamUnitsPerMonth, s.monthIndex);
      const tamNext = interpolate(marketDef.tamUnitsPerMonth, s.monthIndex + 12);
      if (tamNext > tamNow * 1.02 && product.share > 0.01) customerDelta(ctx, product.marketId, "eolGrowing");
      emit(ctx, "productEol", `${product.id} end-of-lifed${returned > 0 ? ` — channel returns ${"$" + Math.round(returned / 1000)}K` : ""}`, {
        productId: product.id,
        channelReturns: returned,
      });
      return null;
    }
    case "raiseRound": {
      if (s.stage === "public") return "you have public markets now — use a secondary offering";
      if (s.finance.activeRaise) return "a raise is already in progress";
      if (s.finance.openTermSheet) return "answer the term sheet on the table first";
      if (s.finance.raiseCooldownMonths > 0) return "investors need a breather (cooldown active)";
      if (!isPosInt(command.ask) || command.ask < MIN_RAISE || command.ask > MAX_RAISE) {
        return `ask must be integer ${MIN_RAISE}..${MAX_RAISE}`;
      }
      charge(ctx, "financingLegal", -eco.financing.legalCost, "round legal/diligence costs");
      s.finance.activeRaise = { ask: command.ask, monthsActive: 0 };
      return null;
    }
    case "acceptTermSheet": {
      const sheet = s.finance.openTermSheet;
      if (!sheet) return "no term sheet on the table";
      // in the garage, accepting the seed sheet IS the founding transaction
      if (s.stage === "garage") incorporate(ctx, sheet);
      else closeRound(ctx, sheet);
      return null;
    }
    case "rejectTermSheet": {
      if (!s.finance.openTermSheet) return "no term sheet on the table";
      s.finance.openTermSheet = null;
      // the raise process continues — maybe a better lead turns up before maxMonths
      emit(ctx, "termSheetExpired", "Term sheet declined — the search for a lead continues.", { rejected: true });
      return null;
    }
    case "fileS1": {
      return fileS1(ctx, command.underwriter);
    }
    case "priceIpo": {
      const proc = s.ipo.process;
      if (!proc || proc.phase !== "pricing") return "no pricing decision is pending";
      if (typeof command.perShare !== "number" || !Number.isFinite(command.perShare) || command.perShare <= 0) {
        return "perShare must be a positive number";
      }
      // clamped into the bankers' range — you price the range, not the fantasy
      priceIpo(ctx, command.perShare);
      return null;
    }
    case "setGuidance": {
      if (!s.street) return "guidance is a public-company ritual";
      if (typeof command.policy !== "number" || !Number.isFinite(command.policy) || Math.abs(command.policy) > 1) {
        return "policy must be a number in [-1, 1] (sandbag..stretch)";
      }
      s.street.guidancePolicy = command.policy;
      return null;
    }
    case "secondaryOffering": {
      // no fixed ceiling — public markets scale with market cap; street.ts caps at what the market absorbs
      if (!isPosInt(command.amount) || command.amount < MIN_RAISE) {
        return `amount must be an integer of at least ${MIN_RAISE}`;
      }
      return secondaryOffering(ctx, command.amount);
    }
    case "buyback": {
      if (!isPosInt(command.amount)) return "amount must be a positive integer";
      return buyback(ctx, command.amount);
    }
    case "setChannelPolicy": {
      if (!(command.marketId in s.markets)) return "unknown market";
      const maxMonths = eco.channel.maxMonths;
      if (typeof command.months !== "number" || !Number.isFinite(command.months) || command.months < 0 || command.months > maxMonths) {
        return `months must be a number 0..${maxMonths} (distributors' shelves are finite)`;
      }
      s.markets[command.marketId]!.channelMonths = command.months;
      return null;
    }
    case "answerAsk": {
      if (typeof command.askId !== "string") return "askId must be a string";
      return answerAsk(ctx, command.askId, command.option);
    }
    case "setCompPolicy": {
      if (typeof command.policy !== "number" || !Number.isFinite(command.policy) || Math.abs(command.policy) > 1) {
        return "policy must be a number in [-1, 1] (below-market..above-market)";
      }
      s.talent.compPolicy = command.policy;
      return null;
    }
    case "setEdaTier": {
      if (!(command.tier in eco.tools.edaTiers)) return "tier must be basic|standard|premium";
      s.tools.edaTier = command.tier;
      return null;
    }
    case "setToolStaff": {
      if (!DISCIPLINES.includes(command.discipline)) return "unknown discipline";
      if (!Number.isInteger(command.engineers) || command.engineers < 0 || command.engineers > MAX_STAFF_PER_PROJECT) {
        return "engineers must be a non-negative integer";
      }
      s.tools.inhouse[command.discipline].staff = command.engineers;
      return null;
    }
    case "acquireRival": {
      if (command.payment !== "cash" && command.payment !== "stock") return "payment must be cash or stock";
      return acquireRival(ctx, command.rivalId, command.payment);
    }
    case "setGarageAlloc": {
      if (s.stage !== "garage") return "the garage is behind you";
      const g = s.garage!;
      for (const [k, v] of Object.entries(command.alloc)) {
        if (!GARAGE_ACTIVITIES.includes(k as (typeof GARAGE_ACTIVITIES)[number])) return `unknown activity ${k}`;
        if (typeof v !== "number" || !Number.isFinite(v) || v < 0) return `alloc.${k} must be a non-negative number`;
      }
      if (command.researchNodeId !== undefined && command.researchNodeId !== null) {
        const tree = activeTree(ctx);
        if (!tree) return "this scenario has no architecture tree";
        let node;
        try {
          node = getTechNode(tree, command.researchNodeId);
        } catch {
          return "unknown tech node";
        }
        if (!node.garageResearchable) return `${node.displayName} needs a real company behind it`;
        if (isResearched(ctx, node.id)) return `${node.displayName} is already researched`;
        if (!prereqsMet(ctx, node)) return `${node.displayName} has unmet prerequisites`;
        g.researchNodeId = command.researchNodeId;
      } else if (command.researchNodeId === null) {
        g.researchNodeId = null;
      }
      // rebuild from validated keys only
      g.alloc = {};
      for (const k of GARAGE_ACTIVITIES) {
        const v = command.alloc[k];
        if (v !== undefined && v > 0) g.alloc[k] = v;
      }
      return null;
    }
    case "schedulePitch": {
      if (s.stage !== "garage") return "you already have investors";
      if (s.finance.openTermSheet) return "answer the sheet on the table first";
      const g = s.garage!;
      const cd = eco.garage.pitch.cooldownMonths;
      if (g.lastPitchMonth !== null && s.monthIndex - g.lastPitchMonth < cd) {
        return "partners' calendars fill up — give it a month";
      }
      runPitch(ctx);
      return null;
    }
    case "foldCompany": {
      if (s.stage !== "garage") return "the company is real now — this isn't a fold, it's a shutdown";
      s.dead = true;
      s.deathMonth = s.monthIndex;
      s.deathReason = "folded";
      emit(ctx, "folded", "You go back to your old job. The napkins go in a drawer.", { month: s.monthIndex });
      return null;
    }
    case "setTreeStaff": {
      const tree = activeTree(ctx);
      if (!tree) return "this scenario has no architecture tree";
      let node;
      try {
        node = getTechNode(tree, command.nodeId);
      } catch {
        return "unknown tech node";
      }
      if (isResearched(ctx, node.id)) return `${node.displayName} is already researched`;
      if (!Number.isInteger(command.engineers) || command.engineers < 0 || command.engineers > MAX_STAFF_PER_PROJECT) {
        return "engineers must be a non-negative integer";
      }
      if (command.engineers === 0) delete s.tree.staffing[node.id];
      else s.tree.staffing[node.id] = command.engineers;
      return null;
    }
    default:
      return "unknown command type";
  }
}

function salaryOf(ctx: TickContext, d: Discipline): number {
  const table = ctx.content.economy.salaries;
  // piecewise-linear on the salary table for this discipline
  return interpolate(
    table.map((p) => ({ m: p.m, v: p[d] })),
    ctx.s.monthIndex,
  );
}

export { salaryOf };
